import React, { useCallback, useMemo, useState } from "react";
import { Form, InputGroup } from "react-bootstrap";
import AsyncSelect from "react-select/async";
import { formatAmount } from "../../../PatientAccounts/utils/patientAccountUtils";
import { convertLineAmount } from "../utils/billingUtils";
import { toMoneyNumber } from "../../../../utils/financeEngine";

const MASTER_SELECT_LIMIT = 80;

const masterSelectStyles = {
	menuPortal: (base) => ({ ...base, zIndex: 10000 }),
	menu: (base) => ({ ...base, zIndex: 10000 }),
	control: (base) => ({
		...base,
		minHeight: 31,
		fontSize: "0.875rem",
	}),
	valueContainer: (base) => ({ ...base, padding: "0 8px" }),
	indicatorsContainer: (base) => ({ ...base, height: 31 }),
};

/**
 * Searchable master dropdown.
 * Prefer `loadMasterOptions` (API search) so opening Billing & Payment never
 * downloads / renders the full lab/service master lists.
 * Falls back to in-memory `items` when no loader is provided (Collect Payment).
 */
export const BillingMasterSelect = ({
	items = [],
	value,
	selectedLabel,
	getItemValue,
	getItemSearchText,
	formatItemLabel,
	isItemDisabled,
	loadMasterOptions,
	onChange,
	placeholder,
	noOptionsMessage,
	isDisabled = false,
	minSearchLength = 2,
}) => {
	const selectedOption = useMemo(() => {
		if (value === null || value === undefined || value === "") return null;
		if (typeof loadMasterOptions === "function") {
			return {
				value,
				label: selectedLabel || String(value),
			};
		}
		const matched = (items || []).find(
			(item) => String(getItemValue(item)) === String(value)
		);
		if (matched) {
			return {
				value: getItemValue(matched),
				label: formatItemLabel(matched),
				data: matched,
			};
		}
		return {
			value,
			label: selectedLabel || String(value),
		};
	}, [
		formatItemLabel,
		getItemValue,
		items,
		loadMasterOptions,
		selectedLabel,
		value,
	]);

	const loadOptions = useCallback(
		async (inputValue) => {
			const q = String(inputValue || "").trim();

			if (typeof loadMasterOptions === "function") {
				if (q.length < minSearchLength) return [];
				try {
					const options = await loadMasterOptions(q);
					return Array.isArray(options) ? options.slice(0, MASTER_SELECT_LIMIT) : [];
				} catch (error) {
					console.error("Error loading master options:", error);
					return [];
				}
			}

			let list = Array.isArray(items) ? items : [];
			if (q) {
				list = list.filter((item) =>
					String(getItemSearchText(item) || "")
						.toLowerCase()
						.includes(q.toLowerCase())
				);
			}
			return list.slice(0, MASTER_SELECT_LIMIT).map((item) => ({
				value: getItemValue(item),
				label: formatItemLabel(item),
				data: item,
				isDisabled: Boolean(isItemDisabled?.(item)),
			}));
		},
		[
			formatItemLabel,
			getItemSearchText,
			getItemValue,
			isItemDisabled,
			items,
			loadMasterOptions,
			minSearchLength,
		]
	);

	return (
		<AsyncSelect
			cacheOptions
			defaultOptions={false}
			loadOptions={loadOptions}
			value={selectedOption}
			onChange={(opt) => onChange?.(opt?.value ?? "", opt)}
			isClearable
			isDisabled={isDisabled}
			placeholder={placeholder}
			classNamePrefix="react-select"
			className="billing-master-select"
			menuPortalTarget={typeof document !== "undefined" ? document.body : null}
			menuPosition="fixed"
			styles={masterSelectStyles}
			noOptionsMessage={({ inputValue }) =>
				typeof noOptionsMessage === "function"
					? noOptionsMessage(inputValue)
					: noOptionsMessage ||
						(!inputValue
							? `Type at least ${minSearchLength} characters...`
							: "No matches")
			}
		/>
	);
};

export const SECTION_VARIANTS = {
	pharmacy: {
		icon: "fa-medkit",
		accent: "pharmacy",
	},
	services: {
		icon: "fa-stethoscope",
		accent: "services",
	},
	lab: {
		icon: "fa-flask",
		accent: "lab",
	},
	treatment: {
		icon: "fa-heartbeat",
		accent: "treatment",
	},
};

export const CollapsibleSection = ({
	title,
	total,
	currency,
	isOpen,
	onToggle,
	children,
	getCurrencySymbol,
	variant = "pharmacy",
	itemCount = 0,
	hideTotal = false,
}) => {
	const config = SECTION_VARIANTS[variant] || SECTION_VARIANTS.pharmacy;

	return (
		<div
			className={`billing-section-card billing-section-card--${config.accent} ${
				isOpen ? "billing-section-card--open" : ""
			}`}
		>
			<button
				type="button"
				className="billing-section-header"
				onClick={onToggle}
				aria-expanded={isOpen}
			>
				<span className="billing-section-header-main">
					<span className={`billing-section-icon billing-section-icon--${config.accent}`}>
						<i className={`fa ${config.icon}`} />
					</span>
					<span className="billing-section-title">
						{title}
						{itemCount > 0 && (
							<span className="billing-section-count-inline">({itemCount})</span>
						)}
					</span>
				</span>
				<span className="billing-section-meta">
					{!hideTotal && (
						<span className="billing-section-total">
							{formatAmount(total, currency, getCurrencySymbol)}
						</span>
					)}
					<i className={`fa fa-chevron-${isOpen ? "up" : "down"} billing-section-chevron`} />
				</span>
			</button>
			{isOpen && <div className="billing-section-body">{children}</div>}
		</div>
	);
};

export const BillingSectionDivider = ({ title }) => (
	<div className="billing-section-divider">
		<span>{title}</span>
	</div>
);

export const AmountInput = ({
	value,
	onChange,
	onFocus,
	onBlur,
	disabled,
	disableScrollProps,
	formatOnBlur = true,
}) => {
	const handleFocus = (e) => {
		onFocus?.(e);
		disableScrollProps?.onFocus?.(e);
	};

	const handleBlur = (e) => {
		if (onBlur) {
			onBlur(e);
		} else if (formatOnBlur) {
			const num = toMoneyNumber(e.target.value || 0);
			onChange(String(num));
		}
		disableScrollProps?.onBlur?.(e);
	};

	return (
		<Form.Control
			type="text"
			size="sm"
			className="billing-amount-input"
			value={value === 0 || value ? String(value) : ""}
			disabled={disabled}
			onFocus={handleFocus}
			onChange={(e) => {
				const raw = e.target.value;
				if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw)) {
					onChange(raw);
				}
			}}
			onBlur={handleBlur}
			onKeyDown={disableScrollProps?.onKeyDown}
		/>
	);
};

export const BillingLineQuantityInput = ({ item, updateFn, disabled, disableScrollProps }) => (
	<Form.Control
		type="number"
		size="sm"
		min={1}
		step={1}
		className="billing-line-quantity-input"
		value={item.quantity ?? 1}
		disabled={disabled}
		onChange={(e) => {
			const raw = e.target.value;
			if (raw === "") {
				updateFn(item.id, 1);
				return;
			}
			const val = parseInt(raw, 10);
			updateFn(item.id, Number.isFinite(val) && val > 0 ? val : 1);
		}}
		{...disableScrollProps}
	/>
);

export const BillingLineAmountInput = ({
	item,
	updateFn,
	disabled,
	disableScrollProps,
	displayCurrency,
	convertCurrency,
	pharmacyCurrency,
	allowedCurrencies,
	getCurrencySymbol,
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [draft, setDraft] = useState("");

	const committedValue = useMemo(() => {
		if (!item?.amount && item?.amount !== 0) return "";
		return String(
			convertLineAmount(
				item.amount,
				item.currency,
				displayCurrency,
				convertCurrency,
				pharmacyCurrency,
				allowedCurrencies
			)
		);
	}, [
		allowedCurrencies,
		convertCurrency,
		displayCurrency,
		item?.amount,
		item?.currency,
		pharmacyCurrency,
	]);

	const handleFocus = () => {
		setIsEditing(true);
		setDraft(committedValue);
	};

	const handleBlur = (e) => {
		const rawValue = e?.target?.value ?? draft;
		const displayAmount = toMoneyNumber(rawValue || 0);
		const nativeAmount = convertLineAmount(
			displayAmount,
			displayCurrency,
			item.currency,
			convertCurrency,
			pharmacyCurrency,
			allowedCurrencies
		);
		updateFn(item.id, nativeAmount);
		setIsEditing(false);
		setDraft("");
	};

	const paymentSymbol = getCurrencySymbol(displayCurrency);

	return (
		<InputGroup size="sm" className="billing-line-amount-input-group">
			<InputGroup.Text className="billing-line-currency-symbol" title={displayCurrency}>
				{paymentSymbol}
			</InputGroup.Text>
			<AmountInput
				value={isEditing ? draft : committedValue}
				disabled={disabled}
				formatOnBlur={false}
				onFocus={handleFocus}
				onChange={setDraft}
				onBlur={handleBlur}
				disableScrollProps={disableScrollProps}
			/>
		</InputGroup>
	);
};

export const SourceBadge = ({ variant, children }) => (
	<span className={`billing-source-badge billing-source-badge--${variant}`}>{children}</span>
);

export const SuggestedInvestigationsBlock = ({ suggestions, t }) => {
	if (!suggestions?.length) return null;

	return (
		<div className="billing-suggested-block">
			<div className="billing-suggested-label">{t("Suggested Investigations")}</div>
			<p className="billing-suggested-hint">
				{t("Doctor suggestions — not billed until added via Add Investigation.")}
			</p>
			<ul className="billing-suggested-list">
				{suggestions.map((item) => (
					<li key={item.id || item.test_id} className="billing-suggested-item">
						<div className="billing-line-name">
							<span className="billing-line-title">{item.test_name || item.name}</span>
							<SourceBadge variant="suggested">{t("Suggested")}</SourceBadge>
						</div>
						{item.suggestion_reason ? (
							<div className="billing-suggested-reason">{item.suggestion_reason}</div>
						) : null}
					</li>
				))}
			</ul>
		</div>
	);
};

export const BillingAmountCell = ({ children, onRemove, removeLabel }) => (
	<div className="billing-line-amount">
		<div className="billing-line-amount-input">{children}</div>
		{onRemove ? (
			<button
				type="button"
				className="billing-line-remove"
				onClick={onRemove}
				aria-label={removeLabel}
				title={removeLabel}
			>
				<i className="fa fa-trash" />
			</button>
		) : null}
	</div>
);

export const BillingPriorPaymentSummaryRows = ({
	t,
	paymentBalance,
	displayCurrency,
	getCurrencySymbol,
}) => {
	if (!paymentBalance?.hasPriorPayment) return null;

	const paidAmount = paymentBalance.paidSoFarBase ?? paymentBalance.paidSoFar;
	const dueAmount = paymentBalance.amountDueBase ?? paymentBalance.amountDue;

	return (
		<>
			<div className="summary-row billing-summary-row--paid">
				<span>{t("Paid")}</span>
				<strong>
					{formatAmount(paidAmount, displayCurrency, getCurrencySymbol)}
				</strong>
			</div>
			<div className="summary-row billing-summary-row--due">
				<span>{t("Balance Due")}</span>
				<strong>
					{formatAmount(dueAmount, displayCurrency, getCurrencySymbol)}
				</strong>
			</div>
		</>
	);
};
